"use client";

import { useBlogTheme } from "@/contexts/blog-theme-context";

interface TableRow {
  name: string;
  score: number;
  cost: number;
  company: "agentica" | "openai" | "anthropic";
  bold: boolean;
}

const data: TableRow[] = [
  {
    name: "Agentica Opus 4.6 (120k) High",
    score: 85.28,
    cost: 6.94,
    company: "agentica",
    bold: true,
  },
  {
    name: "Opus 4.6 (120k) High",
    score: 79.03,
    cost: 3.81,
    company: "anthropic",
    bold: false,
  },
  {
    name: "Agentica GPT 5.2 (XHigh)",
    score: 70.27,
    cost: 5.03,
    company: "agentica",
    bold: true,
  },
  {
    name: "GPT 5.2 (XHigh)*",
    score: 59.81,
    cost: 2.05,
    company: "openai",
    bold: false,
  },
  {
    name: "Agentica Opus 4.5",
    score: 49.58,
    cost: 10.4,
    company: "agentica",
    bold: true,
  },
  {
    name: "Opus 4.5 (32k)",
    score: 28.15,
    cost: 1.37,
    company: "anthropic",
    bold: false,
  },
];

const accentColors = {
  agentica: "#7668c7",
  openai: "#444",
  anthropic: "#9E4530",
};

export function BenchmarkTable() {
  const { resolvedTheme } = useBlogTheme();
  const isDark = resolvedTheme === "dark";

  const styles = {
    container: {
      width: "96%",
      maxWidth: "1200px",
      padding: "16px",
      boxSizing: "border-box" as const,
      position: "relative" as const,
      margin: "0 auto",
    },
    wrapper: {
      position: "relative" as const,
      background: isDark ? "#1a1a1a" : "#FFFDF0",
      borderRadius: "8px",
      overflow: "hidden",
      border: isDark ? "1px solid #2a2a2a" : "none",
      boxShadow: isDark ? "0 0 40px rgba(118,120,227,.12)" : "none",
    },
    header: {
      display: "flex",
      borderBottom: isDark ? "2px solid #2a2a2a" : "2px solid #e0dfd9",
      background: isDark ? "#141414" : "#f5f4f0",
    },
    headerCell: {
      padding: "0.5rem 1rem",
      fontFamily: "'IBM Plex Mono', monospace",
      fontSize: "0.65rem",
      fontWeight: 500,
      color: "#888",
      letterSpacing: "0.08em",
      textTransform: "uppercase" as const,
      textAlign: "center" as const,
    },
    row: {
      display: "flex",
      borderBottom: isDark ? "1px solid #2a2a2a" : "1px solid #e0dfd9",
      position: "relative" as const,
    },
    cell: {
      padding: "0.55rem 1rem",
      display: "flex",
      alignItems: "center",
    },
    cellText: {
      fontFamily: "'IBM Plex Mono', monospace",
      color: isDark ? "#999" : "#555",
      letterSpacing: "-0.01em",
      fontSize: "0.82rem",
      fontWeight: 300,
    },
    accent: {
      position: "absolute" as const,
      left: 0,
      top: 0,
      bottom: 0,
      width: "4px",
      borderRadius: "0 2px 2px 0",
      opacity: 0.6,
    },
  };

  return (
    <div style={styles.container} className="benchmark-table__container">
      <div style={styles.wrapper} className="benchmark-table__wrapper">
        <div style={styles.header} className="benchmark-table__header">
          <div
            style={{
              ...styles.headerCell,
              width: "50%",
              textAlign: "left",
              paddingLeft: "1.5rem",
            }}
          >
            Submission
          </div>
          <div style={{ ...styles.headerCell, width: "28%" }}>
            Public Eval Score (%)
          </div>
          <div style={{ ...styles.headerCell, width: "22%" }}>
            Cost ($/task)
          </div>
        </div>

        {data.map((row, index) => (
          <div
            key={index}
            style={{
              ...styles.row,
              borderBottom:
                index === data.length - 1 ? "none" : styles.row.borderBottom,
            }}
          >
            <div
              style={{
                ...styles.accent,
                background: accentColors[row.company],
              }}
            />
            <div
              style={{
                ...styles.cell,
                width: "50%",
                justifyContent: "flex-start",
                paddingLeft: "1.5rem",
              }}
            >
              <span
                style={{
                  ...styles.cellText,
                  color: row.bold
                    ? isDark
                      ? "#fff"
                      : "#000"
                    : isDark
                      ? "#ccc"
                      : "#1a1a1a",
                  fontWeight: row.bold ? 600 : 400,
                }}
              >
                {row.name}
              </span>
            </div>
            <div
              style={{
                ...styles.cell,
                width: "28%",
                justifyContent: "center",
                borderLeft: isDark ? "1px solid #2a2a2a" : "1px solid #e0dfd9",
              }}
            >
              <span style={styles.cellText}>{row.score}</span>
            </div>
            <div
              style={{
                ...styles.cell,
                width: "22%",
                justifyContent: "center",
                borderLeft: isDark ? "1px solid #2a2a2a" : "1px solid #e0dfd9",
              }}
            >
              <span style={styles.cellText}>{row.cost.toFixed(2)}</span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
